import type { AnchorHTMLAttributes, InputHTMLAttributes, LiHTMLAttributes, ReactNode } from 'react'
import './SettingsParts.css'

/**
 * Piesele comune ale ecranelor din zona de setari, in desenul nou (Customers,
 * Contract Templates, Auth Vault, AI Agent). S-au nascut pe Settings ›
 * Inventoree (`InventoreeParts.tsx`, 20d78a33da si 9853aacc3c) si stau acum
 * aici, fiindca le cere si Settings › Softsee: aceeasi piesa scrisa de doua ori
 * se desparte in doua la a treia schimbare.
 *
 * Numele claselor raman `od-inv-*`. Sunt scrise in markupul a zece ecrane de
 * Inventoree si in probele lor; o redenumire ar fi o zi de cautat prin fisiere
 * pentru un nume mai frumos in foaie, si niciun pixel schimbat pe ecran.
 * `InventoreeParts.tsx` ramane, si trimite incoace.
 */

/**
 * Capul unui ecran: uneltele plutite la dreapta INAINTEA titlului, apoi `h4` si
 * `label.color`, apoi linia. Titlul se scrie ca in portal - un cuvant ingrosat
 * si restul (`<strong>Manage</strong> software`).
 */
export function SettingsHead({ strong, rest, subtitle, tools }: { strong: string; rest?: string; subtitle: ReactNode; tools?: ReactNode }) {
  return (
    <>
      {tools !== undefined && <div className="pull-right od-inv-tools">{tools}</div>}
      <h4>
        <strong>{strong}</strong>
        {rest ? ' ' + rest : ''}
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        {subtitle}
      </label>
      <hr />
    </>
  )
}

/**
 * Bifa portalului: casuta in desenul nou lipita de eticheta ei, intr-un singur
 * `<label>` - clicul pe text bifeaza, si bifa nu mai poate sta peste text.
 *
 * S-a nascut in zona de setari, si pana pe 18 septembrie 2026 arata a desen nou
 * NUMAI acolo: foaia ei cerea `body.od-console`, pe care `Shell` il pune doar
 * pe ecranele de setari. Pusa in fisa aparatului (Agent settings > Operations),
 * aceeasi piesa iesea cu albastrul browserului. Acum foaia se leaga de clasa
 * `od-inv-check`, nu de zona, si piesa arata la fel oriunde e pusa - vezi
 * `SettingsParts.css`. Numele ramane `SettingsCheck` fiindca il scriu deja
 * cincisprezece ecrane; nu inseamna "numai in setari".
 *
 * `disabled` fiindca sunt bife pe care omul le VEDE dar nu le poate muta: una
 * mostenita de la platforma, una a unui drept pe care nu-l are, una ceruta de
 * alta (Local Network Discovery la Operations). Fara ea, fiecare astfel de loc
 * isi scria casuta de mana - exact bifa pe care piesa asta exista s-o stearga.
 *
 * `<input>` ramane nativ si inauntru: formularul trimite aceleasi nume, iar
 * tastatura si cititorul de ecran o gasesc singure. Orice alt atribut al bifei
 * (`form`, `data-*`, `aria-label`, `className`) trece pe `<input>` ca atare.
 * Fara `label`, piesa e doar casuta - pentru celula unui tabel, unde eticheta
 * e capul coloanei; atunci se da `aria-label`.
 */
type BoxProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange' | 'title'> & {
  label?: ReactNode
  /** Pe `<label>`, ca sa acopere si textul. */
  title?: string
  onChange?(on: boolean): void
} & DataAttrs

function Box({ type, label, title, onChange, ...input }: BoxProps & { type: 'checkbox' | 'radio' }) {
  return (
    <label className="od-inv-check" title={title}>
      <input type={type} {...input} onChange={onChange ? (e) => onChange(e.target.checked) : undefined} />
      {label !== undefined && label !== null ? <span>{label}</span> : null}
    </label>
  )
}

export function SettingsCheck(props: BoxProps) {
  return <Box type="checkbox" {...props} />
}

/**
 * Butonul radio al portalului, in acelasi desen ca bifa: aceeasi cerneala,
 * aceeasi marime, acelasi `<label>`. Un grup se face ca in HTML - acelasi
 * `name`, alt `value`. `onChange(true)` vine cand omul il alege pe el.
 */
export function SettingsRadio(props: BoxProps & { value: string | number }) {
  return <Box type="radio" {...props} />
}

/**
 * Coloana din stanga prin care se alege ce se vede in dreapta: felul de licenta,
 * tipul de alarma, categoria de aplicatii, eticheta unui program.
 *
 * Randul ales se cunoaste dupa hartia lui si dupa dunga de cerneala din stanga,
 * exact ca randul aprins din rail-ul de alaturi; restul sunt text linistit.
 * Inainte era `nav-metro-tabs` cu albastrul (65,153,194) scris INLINE pe primul
 * rand - o culoare care nu e nicaieri in zona, si care se aprindea pe un rand
 * care nici macar nu era cel ales.
 */
export function SettingsPicker({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <ul className="od-inv-picker" id={id}>
      {children}
    </ul>
  )
}

/** `data-category`, `data-license`, `data-tag`, `data-alerttype` - cum le scriu machetele. */
type DataAttrs = { [key: `data-${string}`]: string | number | undefined }

/** Un rand al coloanei de alegere. `liProps`/`aProps` duc `data-*`-urile din machete. */
export function SettingsPickerItem({
  label,
  active,
  onPick,
  liProps,
  aProps,
}: {
  label: ReactNode
  active: boolean
  onPick(): void
  liProps?: LiHTMLAttributes<HTMLLIElement> & DataAttrs
  aProps?: AnchorHTMLAttributes<HTMLAnchorElement> & DataAttrs
}) {
  return (
    <li {...liProps}>
      <a
        href="#"
        {...aProps}
        className={active ? 'active' : undefined}
        aria-current={active ? 'true' : undefined}
        onClick={(e) => {
          e.preventDefault()
          onPick()
        }}
      >
        {label}
      </a>
    </li>
  )
}

/** Capul unei grupe din coloana de alegere - se citeste ca un cap, nu ca un rand. */
export function SettingsPickerHeader({ label }: { label: ReactNode }) {
  return <li className="od-inv-picker-header">{label}</li>
}
